// Helpers for the inline-SVG latency sparkline. Pulled out of the
// component so the renderer stays purely declarative -- pad / scale
// once, hand the result to a `<polyline>`, done.

export type SparklineGeometry = {
  // Path data for the line (avg latency over time).
  linePath: string
  // Path data for the filled area underneath the line. Same shape
  // closed at the bottom so we can fill it with a translucent
  // gradient. Empty when there's no data.
  areaPath: string
  // True when at least one bucket has a numeric value -- callers
  // use this to swap in an "empty" placeholder.
  hasData: boolean
  // Y-coordinate of the baseline reference line in viewBox units,
  // already scaled against the same min/max as `linePath`. Null
  // when no baseline was supplied or there's no data to draw
  // against. Returning this from the geometry helper instead of
  // recomputing in the renderer guarantees the line and the
  // baseline share a single y-scale.
  baselineY: number | null
}

export type SparklineOptions = {
  width: number
  height: number
  // Top-bottom padding in user-units so the line never grazes the
  // outer edge (otherwise stroke-width visually clips).
  pad?: number
  // Optional reference value (e.g. p95 baseline) that should land
  // on the same y-scale as `values`. When set, the helper widens
  // its min/max so the baseline always renders inside the strip
  // even if the live data sits entirely on one side of it.
  baseline?: number | null
}

// buildSparklineGeometry takes a fixed-length latency strip
// (oldest-first, nullable values) and produces SVG path strings
// scaled to the viewBox. Nulls are treated as gaps -- we break the
// line at a gap so the strip can read "we had no data here" rather
// than implying a zero-latency probe. Adjacent stretches of values
// are connected; isolated single-value points get rendered as a
// 1px segment so they're still visible.
export function buildSparklineGeometry(
  values: ReadonlyArray<number | null>,
  opts: SparklineOptions,
): SparklineGeometry {
  const { width, height, pad = 2, baseline } = opts
  if (values.length === 0) {
    return { linePath: "", areaPath: "", hasData: false, baselineY: null }
  }

  const numeric = values.filter((v): v is number => v != null && v >= 0)
  if (numeric.length === 0) {
    return { linePath: "", areaPath: "", hasData: false, baselineY: null }
  }

  // Compute the y-axis domain across both the data and the
  // baseline so the line and the reference line share one scale.
  // Without this, a baseline that sits well above the live data
  // would render at the top edge of the strip while the line
  // hugged the bottom -- visually disconnected even though they
  // are spatially related.
  const baselineNumeric =
    baseline != null && Number.isFinite(baseline) && baseline >= 0
      ? baseline
      : null
  const domain =
    baselineNumeric == null ? numeric : [...numeric, baselineNumeric]
  const min = Math.min(...domain)
  const max = Math.max(...domain)
  // Fall back to a unit range when every value is identical so the
  // line lands in the middle of the strip rather than at the top.
  const span = max - min || 1

  const stepX = width / Math.max(1, values.length - 1)
  const innerH = height - pad * 2
  const toY = (v: number) => pad + innerH - ((v - min) / span) * innerH

  const segments: string[][] = []
  let current: string[] = []
  for (let i = 0; i < values.length; i++) {
    const v = values[i]
    if (v == null) {
      if (current.length) segments.push(current)
      current = []
      continue
    }
    const x = i * stepX
    const y = toY(v)
    current.push(
      `${current.length === 0 ? "M" : "L"}${x.toFixed(2)},${y.toFixed(2)}`,
    )
  }
  if (current.length) segments.push(current)

  const linePath = segments.map((s) => s.join(" ")).join(" ")
  const areaPath = segments
    .filter((s) => s.length >= 2)
    .map((seg) => {
      const first = seg[0].slice(1)
      const last = seg[seg.length - 1].slice(1)
      const lastX = last.split(",")[0]
      const firstX = first.split(",")[0]
      return `${seg.join(" ")} L${lastX},${height} L${firstX},${height} Z`
    })
    .join(" ")

  const baselineY = baselineNumeric == null ? null : toY(baselineNumeric)

  return { linePath, areaPath, hasData: true, baselineY }
}
